import React, { useMemo, useState } from 'react';
import { Check, Copy, FileDown, Link2, Loader2 } from 'lucide-react';
import {
  AMPEL_NACHWEIS,
  formatDatum,
  gueltigkeitText,
  teilnahmeAmpel,
  teilnahmeName,
  unterweisungLinkUrl,
} from '../../../services/sifa/kundenportalLogic';
import type { KundenportalDaten, PortalDateiArt } from '../../../services/sifa/kundenportalTypes';
import { EINGABE, HINWEISKASTEN } from '../public/teilnahmeSeiteStil';
import { filtereTeilnahmen, teilnahmenJeUnterweisung, unterweisungTitel } from './kundenportalSeiteLogic';

/**
 * Kurse mit aktivem Link zum Weiterleiten und die Teilnahmen je Person:
 * Name, Kurs, Datum, bestanden, gueltig bis (Ampel), Zertifikat-Download.
 * Kein Bearbeiten — Kurse pflegt die SiFa.
 */
export const UnterweisungenTab: React.FC<{
  daten: KundenportalDaten;
  dateiBusyId: string | null;
  onDatei: (art: PortalDateiArt, id: string) => void;
}> = ({ daten, dateiBusyId, onDatei }) => {
  const [kopiertId, setKopiertId] = useState<string | null>(null);
  const [filterKurs, setFilterKurs] = useState('');
  const [suche, setSuche] = useState('');
  const zaehler = useMemo(() => teilnahmenJeUnterweisung(daten.teilnahmen), [daten.teilnahmen]);
  const teilnahmen = useMemo(
    () => filtereTeilnahmen(daten.teilnahmen, { unterweisungId: filterKurs, suche }, daten.mitarbeiter),
    [daten.teilnahmen, daten.mitarbeiter, filterKurs, suche],
  );

  const kurslink = (linkToken: string) => unterweisungLinkUrl(window.location.origin, window.location.pathname, linkToken);

  const kopieren = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setKopiertId(id);
      window.setTimeout(() => setKopiertId((x) => (x === id ? null : x)), 2000);
    } catch {
      window.prompt('Link kopieren:', url);
    }
  };

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h2 className="text-sm font-black uppercase tracking-wide text-slate-400">Unterweisungen</h2>
        {daten.unterweisungen.length === 0 ? (
          <div className={HINWEISKASTEN}>Es ist noch keine Unterweisung freigeschaltet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {daten.unterweisungen.map((u) => {
              const z = zaehler[u.id] || { gesamt: 0, bestanden: 0 };
              const url = u.linkToken ? kurslink(u.linkToken) : null;
              return (
                <div key={u.id} className="border border-slate-200 rounded-xl p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-slate-800">Nr. {u.nummer} — {u.titel || 'ohne Titel'}</p>
                    {url ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full shrink-0">
                        <Link2 className="w-3 h-3" /> Link aktiv
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full shrink-0">
                        {u.status === 'archiviert' ? 'archiviert' : 'Link aus'}
                      </span>
                    )}
                  </div>
                  {u.beschreibung && <p className="text-sm text-slate-600 line-clamp-3">{u.beschreibung}</p>}
                  <p className="text-xs text-slate-500">
                    Gültigkeit {gueltigkeitText(u.gueltigMonate)}{u.dauerMin ? ` · ca. ${u.dauerMin} min` : ''} · {z.bestanden} bestanden von {z.gesamt}
                  </p>
                  {url && (
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => void kopieren(u.id, url)}
                        className="min-h-[44px] w-full sm:w-auto px-3 flex items-center justify-center gap-2 text-sm font-semibold rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50"
                      >
                        {kopiertId === u.id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        {kopiertId === u.id ? 'Kopiert' : 'Link an Mitarbeiter weiterleiten'}
                      </button>
                      <p className="text-[11px] text-slate-400 break-all select-all">{url}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-black uppercase tracking-wide text-slate-400">Teilnahmen</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={filterKurs} onChange={(e) => setFilterKurs(e.target.value)} className={`${EINGABE} sm:w-64`} aria-label="Kurs filtern">
            <option value="">Alle Unterweisungen</option>
            {daten.unterweisungen.map((u) => (
              <option key={u.id} value={u.id}>Nr. {u.nummer} — {u.titel}</option>
            ))}
          </select>
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name suchen" className={EINGABE} aria-label="Teilnahmen nach Name suchen" />
        </div>

        {teilnahmen.length === 0 ? (
          <div className={HINWEISKASTEN}>{daten.teilnahmen.length === 0 ? 'Noch keine Teilnahme.' : 'Keine Teilnahme passt zum Filter.'}</div>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
            {teilnahmen.map((t) => {
              const stufe = t.bestanden ? teilnahmeAmpel(t.gueltigBis) : 'ohne';
              const ampel = t.bestanden ? AMPEL_NACHWEIS[stufe] : { label: 'nicht bestanden', klasse: 'text-red-700 bg-red-50 border-red-200', punkt: 'bg-red-500' };
              return (
                <li key={t.id} className="p-3 flex items-start gap-3">
                  <span className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${ampel.punkt}`} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{teilnahmeName(t, daten.mitarbeiter)}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {unterweisungTitel(daten, t.unterweisungId)} · {t.abgeschlossenAm ? formatDatum(t.abgeschlossenAm) : 'ohne Abschluss'}
                      {t.zertifikatNr ? ` · ${t.zertifikatNr}` : ''}
                    </p>
                    <p className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[11px] font-bold border rounded-full ${ampel.klasse}`}>
                      {ampel.label}
                      {t.bestanden && t.gueltigBis ? ` · bis ${formatDatum(t.gueltigBis)}` : t.bestanden ? ' · ohne Ablauf' : ''}
                    </p>
                  </div>
                  {t.zertifikatVorhanden && (
                    <button
                      type="button"
                      onClick={() => onDatei('zertifikat', t.id)}
                      disabled={!!dateiBusyId}
                      title="Zertifikat herunterladen"
                      className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-semibold rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 shrink-0"
                    >
                      {dateiBusyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                      <span className="hidden sm:inline">Zertifikat</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};
